import React from "react";
import {
  GeneralTask,
  ProjectTask,
  OverdueItem,
  RecentAssignment,
  badgeColorByPriority,
  badgeColorByStatus,
  fmtDate,
} from "./UserHomeApi";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faListCheck, faTriangleExclamation, faTasks } from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import {TaskDTO,TaskApi} from "../project/task/TaskApi";
import {GeneralTaskDTO,GeneralTaskApi} from "../general-task/GeneralTaskApi";
/* ============================
 * Reuse your existing action components
 * Adjust import paths to your project structure if needed.
 * ============================ */
import GeneralTaskActions from "../general-task/GeneralTaskAction"; // <-- your provided component
import ProjectTaskActions from "../project/task/ProjectTaskActions"; // <-- your provided component

/* ============================
 * Minimal adapter types to satisfy action components
 * (We pass what they need from the KPI rows.)
 * ============================ */

// For GeneralTaskActions: it expects GeneralTaskReportDTO-like shape.
// We’ll build a super-set containing required fields from KPI payload.
type GeneralTaskForAction = {
  id: string;
  title: string;
  status: string;
  priority?: string | null;
};

// For ProjectTaskActions: it expects NestedTaskDTO-like shape.
type ProjectTaskForAction = {
  id: string;
  title: string;
  status: string;
  projectId?: string | null;
  projectName?: string | null;
  priority?: string | null;
};

/* ============================
 * General Tasks Table
 * ============================ */
export const GeneralTasksTable: React.FC<{ data: GeneralTask[]; withActions?: boolean , onRetry?: () => void}> = ({
  data,
  withActions = false,
  onRetry = () => {}, // Default to no-op if not provided
}) => {
  const navigate = useNavigate();

  // Handlers used by your existing GeneralTaskActions component
  const handleDelete = async (id: string) => {
    if (confirm('Are you sure you want to delete this task?')) {
        try {
            await GeneralTaskApi.delete(id);
         
            onRetry();  // or retry()
          } catch (error) {
            console.error("Error deleting task:", error);
          }
    }
  };

    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const tenantId = user?.tenantId || '';
    const tenantName = user?.tenantName || '';
    const userId = user?.id || '';



      const handleGeneralTaskCreate = (parentTask: GeneralTaskDTO, levelNumber: string) => {
        navigate('/general-tasks/create', {
          state: {
            parentId: parentTask.id,
            title: parentTask.title,
            description: parentTask.description,
            status: parentTask.status,
            levelNumber,
          },
        });
      };
  return (
    <div className="rounded-2xl border bg-white shadow-sm">
      <div className="px-4 py-3 border-b flex items-center gap-2">
        <FontAwesomeIcon icon={faListCheck} />
        <h3 className="font-semibold">General Tasks</h3>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-left">
            <tr>
              <th className="px-4 py-2">Title</th>
              <th className="px-4 py-2">Status</th>
              <th className="px-4 py-2">Priority</th>
              <th className="px-4 py-2">Category</th>
              <th className="px-4 py-2">Due</th>
              <th className="px-4 py-2">Hours (Est/Act)</th>
              {withActions && <th className="px-4 py-2 w-56">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {data.map((t) => {
              const actionTask: GeneralTaskForAction = {
                id: t.id,
                title: t.title,
                status: t.status,
                priority: t.priority ?? null,
              };
              return (
                <tr key={t.id} className="border-t">
                  <td className="px-4 py-2">{t.title}</td>
                  <td className="px-4 py-2">
                    <span
                      className={`inline-block border text-xs px-2 py-0.5 rounded-lg ${badgeColorByStatus(
                        t.status
                      )}`}
                    >
                      {t.status}
                    </span>
                  </td>
                  <td className="px-4 py-2">
                    <span
                      className={`inline-block border text-xs px-2 py-0.5 rounded-lg ${badgeColorByPriority(
                        t.priority
                      )}`}
                    >
                      {t.priority ?? "-"}
                    </span>
                  </td>
                  <td className="px-4 py-2">{t.category}</td>
                  <td className="px-4 py-2">{fmtDate(t.due_date)}</td>
                  <td className="px-4 py-2">
                    {t.estimated_hours ?? 0} / {t.actual_hours ?? 0}
                  </td>

                  {withActions && (
                    <GeneralTaskActions
                      // The action cell renders inside a <td>, so we keep its layout intact
                      task={actionTask as any}
                      levelNumber={"1"} // top-level in dashboard list
                      maxLevelReached={false}
                      handleDelete={handleDelete}
                      handleCreateTask={handleGeneralTaskCreate}
                    />
                  )}
                </tr>
              );
            })}

            {data.length === 0 && (
              <tr>
                <td colSpan={withActions ? 7 : 6} className="px-4 py-3 text-center text-slate-500">
                  No records
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

/* ============================
 * Project Tasks Table
 * ============================ */
export const ProjectTasksTable: React.FC<{ data: ProjectTask[]; withActions?: boolean, onRetry?: () => void}> = ({
  data,
  withActions = false,
  onRetry = () => {}, 
}) => {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const tenantId = user?.tenantId || '';
  const tenantName = user?.tenantName || '';
  const userId = user?.id || '';
  // Handlers used by your existing ProjectTaskActions component
   const handleDelete = async (id: string) => {
     if (confirm('Are you sure you want to delete this project task?')) {
       try {
        await TaskApi.delete(id);
     
        onRetry();  // or retry()
      } catch (error) {
        console.error("Error deleting task:", error);
      }
     }
   };
  const handleProjectTaskCreate = (task: TaskDTO, levelNumber: string) => {
    navigate('/project-tasks/create', {
      state: {
        tenantId,
        tenantName,
        projectName: task.projectName,
        parentId: task.id,
        title: task.title,
        projectId: task.projectId,
        contractId: task.contractId,
        description: task.description,
        levelNumber,
      },
    });
  };
  

  return (
    <div className="rounded-2xl border bg-white shadow-sm">
      <div className="px-4 py-3 border-b flex items-center gap-2">
        <FontAwesomeIcon icon={faTasks} />
        <h3 className="font-semibold">Project Tasks</h3>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-left">
            <tr>
              <th className="px-4 py-2">Title</th>
              <th className="px-4 py-2">Project</th>
              <th className="px-4 py-2">Status</th>
              <th className="px-4 py-2">Given Dates</th>
              <th className="px-4 py-2">Est. Amount (w/tax)</th>
              {withActions && <th className="px-4 py-2 w-56">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {data.map((t) => {
              const actionTask: ProjectTaskForAction = {
                id: t.id,
                title: t.title,
                status: t.status,
                projectId: t.project_id ?? (t as any).projectId ?? null,
                projectName: t.project_name ?? (t as any).projectName ?? null,
                priority: t.priority ?? null,
              };

              return (
                <tr key={t.id} className="border-t">
                  <td className="px-4 py-2">{t.title}</td>
                  <td className="px-4 py-2">{t.project_name ?? "-"}</td>
                  <td className="px-4 py-2">
                    <span
                      className={`inline-block border text-xs px-2 py-0.5 rounded-lg ${badgeColorByStatus(
                        t.status
                      )}`}
                    >
                      {t.status}
                    </span>
                  </td>
                  <td className="px-4 py-2">
                    {fmtDate(t.given_start_date)} → {fmtDate(t.given_end_date)}
                  </td>
                  <td className="px-4 py-2">
                    {t.estimated_total_amount_with_tax?.toLocaleString() ?? 0}
                  </td>

                  {withActions && (
                    <ProjectTaskActions
                      task={actionTask as any}
                      levelNumber={"1"}
                      maxLevelReached={false}
                      handleDelete={handleDelete}
                      handleCreateTask={handleProjectTaskCreate}
                    />
                  )}
                </tr>
              );
            })}

            {data.length === 0 && (
              <tr>
                <td colSpan={withActions ? 6 : 5} className="px-4 py-3 text-center text-slate-500">
                  No records
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

/* ============================
 * Overdue (utility)
 * ============================ */
export const OverdueTable: React.FC<{ data: OverdueItem[]; title?: string }> = ({
  data,
  title = "Overdue",
}) => {
  return (
    <div className="rounded-2xl border bg-white shadow-sm">
      <div className="px-4 py-3 border-b flex items-center gap-2 text-red-700">
        <FontAwesomeIcon icon={faTriangleExclamation} />
        <h3 className="font-semibold">{title}</h3>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-left">
            <tr>
              <th className="px-4 py-2">Title</th>
              <th className="px-4 py-2">Entity Type</th>
              <th className="px-4 py-2">Due</th>
              <th className="px-4 py-2">Days Overdue</th>
            </tr>
          </thead>
          <tbody>
            {data.map((o, idx) => (
              <tr key={`${o.entity_id}-${idx}`} className="border-t">
                <td className="px-4 py-2">{o.title}</td>
                <td className="px-4 py-2">{o.entity_type}</td>
                <td className="px-4 py-2">{fmtDate(o.due_date)}</td>
                <td className="px-4 py-2 text-red-700 font-semibold">{o.days_overdue}</td>
              </tr>
            ))}
            {data.length === 0 && (
              <tr>
                <td colSpan={4} className="px-4 py-3 text-center text-slate-500">
                  No overdue items 🎉
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

/* ============================
 * Activity Feed (unchanged)
 * ============================ */
export const ActivityFeed: React.FC<{ data: RecentAssignment[] }> = ({ data }) => {
  return (
    <div className="rounded-2xl border bg-white shadow-sm">
      <div className="px-4 py-3 border-b">
        <h3 className="font-semibold">Recent Assignments</h3>
      </div>
      <ul className="divide-y">
        {data.map((a) => (
          <li key={a.id} className="px-4 py-3 flex items-center justify-between">
            <div>
              <p className="font-medium">{a.entity_type}</p>
              <p className="text-xs text-slate-500">{a.comments ?? "—"}</p>
            </div>
            <div className="text-right">
              <p className="text-sm">{a.status}</p>
              <p className="text-xs text-slate-500">{fmtDate(a.assigned_at)}</p>
            </div>
          </li>
        ))}
        {data.length === 0 && (
          <li className="px-4 py-6 text-center text-slate-500">No recent activity</li>
        )}
      </ul>
    </div>
  );
};